import { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type AppTab = 'for-you' | 'library' | 'recording-studio' | 'my-shares' | 'my-channel' | 'channel-directory' | 'recycle-bin';

interface AudioPreferences {
  noiseSuppression: boolean;
  echoCancellation: boolean;
  autoGainControl: boolean;
  systemAudio: boolean;
}

interface NavigationPreferences {
  defaultTab: AppTab;
  compactSidebar: boolean;
  quickRecordShortcut: boolean;
}

interface TranscriptionPreferences {
  enabled: boolean;
  autoStart: boolean;
  timestamps: boolean;
  language: string;
}

/** Bump when navigation defaults change so legacy localStorage can migrate once. */
const PREFERENCES_SCHEMA_VERSION = 2;

export interface PreferencesState {
  schemaVersion: number;
  navigation: NavigationPreferences;
  audio: AudioPreferences;
  transcription: TranscriptionPreferences;
}

interface PreferencesContextValue {
  preferences: PreferencesState;
  setDefaultTab: (tab: AppTab) => void;
  updateNavigation: (payload: Partial<NavigationPreferences>) => void;
  updateAudioPreferences: (payload: Partial<AudioPreferences>) => void;
  updateTranscriptionPreferences: (payload: Partial<TranscriptionPreferences>) => void;
}

const STORAGE_KEY = 'airecording.preferences';

const defaultPreferences: PreferencesState = {
  schemaVersion: PREFERENCES_SCHEMA_VERSION,
  navigation: {
    defaultTab: 'recording-studio',
    compactSidebar: false,
    quickRecordShortcut: true,
  },
  audio: {
    noiseSuppression: true,
    echoCancellation: true,
    autoGainControl: true,
    systemAudio: true,
  },
  transcription: {
    enabled: true,
    autoStart: true,
    timestamps: true,
    language: 'en',
  },
};

const PreferencesContext = createContext<PreferencesContextValue | undefined>(undefined);

const mergePreferences = (stored: Partial<PreferencesState> | null): PreferencesState => {
  if (!stored) return defaultPreferences;

  const storedSchemaVersion =
    typeof stored.schemaVersion === 'number' ? stored.schemaVersion : 1;

  const merged: PreferencesState = {
    ...defaultPreferences,
    ...stored,
    schemaVersion: typeof stored.schemaVersion === 'number' ? stored.schemaVersion : defaultPreferences.schemaVersion,
    navigation: {
      ...defaultPreferences.navigation,
      ...stored.navigation,
    },
    audio: {
      ...defaultPreferences.audio,
      ...stored.audio,
    },
    transcription: {
      ...defaultPreferences.transcription,
      ...stored.transcription,
    },
  };

  if (storedSchemaVersion < PREFERENCES_SCHEMA_VERSION) {
    if (merged.navigation.defaultTab === 'library') {
      merged.navigation = { ...merged.navigation, defaultTab: 'recording-studio' };
    }
    merged.schemaVersion = PREFERENCES_SCHEMA_VERSION;
  }
  // The Meetings page (sample data only) was removed: a saved start page there opens the studio.
  if ((merged.navigation.defaultTab as string) === 'meetings') {
    merged.navigation = { ...merged.navigation, defaultTab: 'recording-studio' };
  }

  return merged;
};

const loadPreferences = (): PreferencesState => {
  if (typeof window === 'undefined') return defaultPreferences;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return defaultPreferences;
    const parsed = JSON.parse(stored) as Partial<PreferencesState>;
    return mergePreferences(parsed);
  } catch (error) {
    console.warn('[Preferences] Could not parse stored preferences', error);
    return defaultPreferences;
  }
};

export const PreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preferences, setPreferences] = useState<PreferencesState>(loadPreferences);

  // Persist preferences
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.warn('[Preferences] Failed to persist preferences', error);
    }
  }, [preferences]);

  const setDefaultTab = (tab: AppTab) => {
    setPreferences((prev) => ({
      ...prev,
      navigation: { ...prev.navigation, defaultTab: tab },
    }));
  };

  const updateNavigation = (payload: Partial<NavigationPreferences>) => {
    setPreferences((prev) => ({
      ...prev,
      navigation: { ...prev.navigation, ...payload },
    }));
  };

  const updateAudioPreferences = (payload: Partial<AudioPreferences>) => {
    setPreferences((prev) => ({
      ...prev,
      audio: { ...prev.audio, ...payload },
    }));
  };

  const updateTranscriptionPreferences = (payload: Partial<TranscriptionPreferences>) => {
    setPreferences((prev) => ({
      ...prev,
      transcription: { ...prev.transcription, ...payload },
    }));
  };

  const value = useMemo<PreferencesContextValue>(
    () => ({
      preferences,
      setDefaultTab,
      updateNavigation,
      updateAudioPreferences,
      updateTranscriptionPreferences,
    }),
    [preferences]
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
};

export const usePreferences = (): PreferencesContextValue => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};
